import React from 'react';
import { Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface ActionButtonProps {
  title: string;
  description: string;
  secondaryDescription?: string;
  isCompleted: boolean;
  onClick?: () => void;
  disabled?: boolean;
  isReport?: boolean;
  route?: string;
  isSuccess?: boolean;
  maxEvaluators?: number;
  currentEvaluators?: number;
  allEvaluationsCompleted?: boolean;
  allContextualized?: boolean;
  evaluatorsFinalized?: boolean;
  reportAvailable?: boolean;
  onboardingRequired?: boolean;
}

export default function ActionButton({ 
  title, 
  description,
  secondaryDescription,
  isCompleted, 
  onClick,
  disabled = false,
  isReport = false,
  route,
  isSuccess = false,
  maxEvaluators,
  currentEvaluators,
  allEvaluationsCompleted,
  allContextualized,
  evaluatorsFinalized = false,
  reportAvailable = false,
  onboardingRequired = false
}: ActionButtonProps) {
  const navigate = useNavigate();
  
  const handleClick = () => {
    if (disabled || (isReport && !reportAvailable) || onboardingRequired) return;
    if (route) {
      navigate(route);
    } else if (onClick) {
      onClick();
    }
  };

  const isDisabled = disabled || (isReport && !reportAvailable) || onboardingRequired;
  const isReportPending = isReport && !disabled && !reportAvailable;
  const isReportAvailable = isReport && reportAvailable;

  const buttonStyle = isCompleted || isSuccess || evaluatorsFinalized
    ? 'bg-green-50 border-green-200 cursor-not-allowed'
    : isDisabled && !isReportPending
    ? 'bg-gray-50 border-gray-200 cursor-not-allowed'
    : isReportAvailable
    ? 'bg-white hover:bg-success/5 cursor-pointer report-button-enabled glow-border'
    : isReport
    ? `bg-white border-primary hover:bg-primary/5 cursor-pointer ${isReportPending ? 'report-button-enabled' : ''}`
    : onboardingRequired
    ? 'bg-gray-50 border-amber-200 cursor-not-allowed'
    : 'bg-white border-primary-action hover:bg-primary-action/5 cursor-pointer';

  const iconContainerStyle = isCompleted || isSuccess || evaluatorsFinalized || isReportAvailable
    ? 'bg-success/10' 
    : isDisabled && !isReportPending
    ? 'bg-gray-200'
    : onboardingRequired
    ? 'bg-amber-200/20'
    : isReport
    ? 'bg-primary/10'
    : 'bg-primary-action/10';

  const iconStyle = isCompleted || isSuccess || evaluatorsFinalized || isReportAvailable
    ? 'border-success' 
    : isDisabled && !isReportPending
    ? 'border-gray-300'
    : onboardingRequired
    ? 'border-amber-400'
    : isReport
    ? 'border-primary'
    : 'border-primary-action';

  return (
    <button
      onClick={handleClick}
      className={`w-full p-6 rounded-xl border-2 transition-all relative overflow-hidden ${buttonStyle}`}
      disabled={isCompleted || isDisabled || isSuccess || evaluatorsFinalized}
    >
      <div className="flex items-start space-x-4">
        <div className={`rounded-full p-2 ${iconContainerStyle}`}>
          {isCompleted || isSuccess || evaluatorsFinalized || isReportAvailable ? (
            <Check className="w-6 h-6 text-success" />
          ) : isDisabled && !isReportPending ? (
            <div className="w-6 h-6 rounded-full border-4 border-gray-300" />
          ) : (
            <div className={`w-6 h-6 rounded-full border-4 ${iconStyle}`} />
          )}
        </div>
        <div className="flex-1">
          <h3 className={`font-semibold ${
            isCompleted || (isDisabled && !isReportPending) || isSuccess || evaluatorsFinalized ? 'text-gray-500' : 'text-gray-900'
          }`}>
            {title}
          </h3>
          <p className={`mt-1 text-sm ${
            isCompleted || (isDisabled && !isReportPending) || isSuccess || evaluatorsFinalized ? 'text-gray-400' : 'text-gray-600'
          }`}>
            {currentEvaluators !== undefined && maxEvaluators !== undefined && typeof allEvaluationsCompleted === 'number'
              ? `${evaluatorsFinalized ? currentEvaluators : allEvaluationsCompleted} of ${currentEvaluators} evaluators have provided feedback`
              : description
            }
          </p>
          {secondaryDescription && (
            <p className={`mt-1 text-sm ${
              isCompleted || (isDisabled && !isReportPending) || isSuccess || evaluatorsFinalized ? 'text-gray-400' : 'text-gray-600'
            }`}>
              {secondaryDescription}
            </p>
          )}
          {!isDisabled && !isReportPending && !isCompleted && !isSuccess && !evaluatorsFinalized && (
            <span className={`inline-block mt-2 text-sm font-medium ${
              isReportAvailable ? 'text-success' : isReport ? 'text-primary' : 'text-primary-action'
            }`}>
              {isReportAvailable ? 'View Report →' : 'Start Now →'}
            </span>
          )}
          {isReportPending && (
            <span className="inline-block mt-2 text-sm font-medium text-primary">
              Your report is being generated
            </span>
          )}
          {isReport && !reportAvailable && !isReportPending && disabled && (
            <span className="inline-block mt-2 text-sm font-medium text-gray-500">
              Report not available yet
            </span>
          )}
          {onboardingRequired && (
            <span className="inline-block mt-2 text-sm font-medium text-amber-600">
              Complete onboarding tutorial first
            </span>
          )}
        </div>
      </div>
    </button>
  );
}